import { Assessment } from '#src/models/assessments/assessment.ts'
import { ParticipantNote } from '#src/models/participantNote.ts'
import type { VisitNote } from '#src/models/visitNote.ts'

/* The three shapes a Composition takes on the participant's notes list: an
 * Elation-synced note, an Orbit-native note, and an assessment. */
export type NamedNote = VisitNote | ParticipantNote | Assessment

/* How to read text out of note markup. The rule for naming a note is one
 * thing; turning its HTML (or serialized TipTap document) into text is another,
 * and that one needs a parser: `DOMParser` in the browser, `html-to-text` on
 * the server, and a TipTap render for JSON content that each package wires up
 * with its own extension set. So the rule takes its readers as an argument
 * rather than importing a parser into this package, and each side hands in the
 * pair it already keeps in step with the other. */
export interface NoteTextReaders {
  /* The first line a reader sees in note content, display-ready — trimmed and
   * already cut to length — or undefined when the content has none. */
  firstLine: (content: string) => string | undefined
  /* The visible text of an HTML fragment. */
  text: (html: string) => string
}

const UNTITLED = 'Untitled'

/* The name the participant's notes list shows for a note. Every other surface
 * that names a note — an AI proposal's source chip, the note tools' listings,
 * the search tool's hits — has to read identically to the row a clinician
 * would open, so the rule lives here once and each surface delegates to it.
 *
 * Elation notes title off their AI title, Orbit-native notes off their first
 * content line, and assessments off their stored title. That is the list's
 * rule, signed or not: a clinician told "this note is unsigned, here is the
 * link" must find it in the list under the same name. */
export function noteName(note: NamedNote, read: NoteTextReaders): string {
  if (note instanceof ParticipantNote) {
    return participantNoteName(note, read)
  }
  const title = read.text(
    note instanceof Assessment ? note.title : note.displayTitle
  )
  return title.trim() || UNTITLED
}

/* An Orbit-native note is named by its first content line — the note's own
 * heading — NOT by its AI-generated `aiTitle`, which stays for Elation notes.
 * The first line is what the clinician wrote at the top, so it stays stable
 * and recognizable; the AI title is still surfaced as the row summary. */
export function participantNoteName(
  note: { content?: string | undefined } | undefined | null,
  read: Pick<NoteTextReaders, 'firstLine'>
): string {
  const content = note?.content
  const line = content ? read.firstLine(content) : undefined
  return line || UNTITLED
}
